import React, { useRef, useState } from 'react';
import {
  downloadCategoriesTemplate,
  downloadSubcategoriesTemplate,
  downloadFamiliesTemplate,
  exportCategoriesToExcel,
  exportSubcategoriesToExcel,
  exportFamiliesToExcel,
  analyzeCategoriesImport,
  analyzeSubcategoriesImport,
  analyzeFamiliesImport,
  executeCategoriesImport,
  executeSubcategoriesImport,
  executeFamiliesImport,
  summarizeImportResult,
} from '../../utils/classificationExcel';
import ClassificationImportPreviewModal from './ClassificationImportPreviewModal';
import { classificationService } from '../../services/classificationService';
import { useCanMutate } from '../../hooks/useCanMutate';

const btnClass =
  'px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors';

/**
 * Barra: descargar plantilla (.xlsx) e importar con vista previa (códigos en servidor).
 * Opcional: apiService (clasificación de recetas), filenameSuffix (nombre de plantilla), importTitleSuffix (modal).
 */
const ClassificationExcelToolbar = ({
  variant,
  onImported,
  setToast,
  apiService = null,
  filenameSuffix = '',
  importTitleSuffix = '',
  exportRows = null,
  exportContext = null,
}) => {
  const canMutate = useCanMutate();
  const svc = apiService ?? classificationService;
  const templateOpts = filenameSuffix ? { filenameSuffix } : {};
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewState, setPreviewState] = useState(null);
  const [confirming, setConfirming] = useState(false);

  if (!canMutate) {
    return null;
  }

  const closePreview = () => {
    if (confirming) return;
    setPreviewOpen(false);
    setPreviewState(null);
  };

  const download = async () => {
    try {
      setBusy(true);
      if (variant === 'categories') await downloadCategoriesTemplate(templateOpts);
      else if (variant === 'subcategories') await downloadSubcategoriesTemplate(templateOpts);
      else await downloadFamiliesTemplate(templateOpts);
      setToast({
        isOpen: true,
        message: 'Plantilla descargada. Solo fila 1 (instrucciones) y fila 2 (cabeceras); datos desde fila 3.',
        type: 'success',
      });
    } catch {
      setToast({ isOpen: true, message: 'No se pudo generar la plantilla Excel.', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const handleExport = async () => {
    const rows = exportRows ?? [];
    if (rows.length === 0) {
      setToast({ isOpen: true, message: 'No hay registros para exportar.', type: 'warning' });
      return;
    }
    try {
      setBusy(true);
      if (variant === 'categories') {
        await exportCategoriesToExcel(rows, templateOpts);
      } else if (variant === 'subcategories') {
        await exportSubcategoriesToExcel(rows, exportContext?.categories ?? [], templateOpts);
      } else {
        await exportFamiliesToExcel(
          rows,
          exportContext?.categories ?? [],
          exportContext?.subcategories ?? [],
          templateOpts
        );
      }
      setToast({
        isOpen: true,
        message: `Exportados ${rows.length} registro(s) a Excel.`,
        type: 'success',
      });
    } catch {
      setToast({ isOpen: true, message: 'No se pudo exportar a Excel.', type: 'error' });
    } finally {
      setBusy(false);
    }
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    setBusy(true);
    try {
      let analysis;
      if (variant === 'categories') analysis = await analyzeCategoriesImport(file);
      else if (variant === 'subcategories') analysis = await analyzeSubcategoriesImport(file, svc);
      else analysis = await analyzeFamiliesImport(file, svc);

      if (!analysis.success) {
        setToast({
          isOpen: true,
          message: analysis.fatalMessage || 'No se pudo leer el archivo.',
          type: 'error',
        });
        return;
      }

      setPreviewState(analysis);
      setPreviewOpen(true);
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'No se pudo leer el archivo. Use una plantilla .xlsx.',
        type: 'error',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleConfirmImport = async () => {
    if (!previewState?.success) return;

    setConfirming(true);
    try {
      let exec;
      if (variant === 'categories') exec = await executeCategoriesImport(previewState.validTasks, svc);
      else if (variant === 'subcategories') exec = await executeSubcategoriesImport(previewState.validTasks, svc);
      else exec = await executeFamiliesImport(previewState.validTasks, svc);

      const merged = { ...exec, skipped: previewState.emptyCount };
      const message = summarizeImportResult(merged);
      const hasErr = merged.errors.length > 0;
      const type =
        !merged.created && hasErr ? 'error' : hasErr ? 'warning' : 'success';

      setToast({ isOpen: true, message, type });
      setPreviewOpen(false);
      setPreviewState(null);
      if (merged.created > 0) onImported?.();
    } catch (err) {
      setToast({
        isOpen: true,
        message: err?.message || 'Error al importar.',
        type: 'error',
      });
    } finally {
      setConfirming(false);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" className={btnClass} onClick={download} disabled={busy || confirming}>
          Descargar plantilla Excel
        </button>
        <button
          type="button"
          className={btnClass}
          onClick={() => inputRef.current?.click()}
          disabled={busy || confirming}
        >
          {busy ? 'Leyendo…' : 'Importar desde Excel'}
        </button>
        <button type="button" className={btnClass} onClick={handleExport} disabled={busy || confirming}>
          Exportar a Excel
        </button>
        <input
          ref={inputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={onFileChange}
        />
      </div>

      <ClassificationImportPreviewModal
        isOpen={previewOpen}
        onClose={closePreview}
        variant={variant}
        preview={previewState}
        onConfirm={handleConfirmImport}
        confirming={confirming}
        titleSuffix={importTitleSuffix}
      />
    </>
  );
};

export default ClassificationExcelToolbar;
